import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { CalendarRange, Clapperboard, HelpCircle, Layers3, TimerReset } from "lucide-react";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { cloneBim4DPricingRules } from "../../lib/bim4dPricingDefaults";
import { getApiBase } from "../../lib/apiBase";

const groups = [
  { key: "surfaceSettings", title: "Paramètres surface" },
  { key: "taskSettings", title: "Paramètres tâches" },
  { key: "deliverables", title: "Livrables" },
  { key: "siteFollowUp", title: "Suivi de chantier" },
];

const fieldHelp = {
  surfaceSettings: {
    minimumSurface: "Surface minimale retenue dans le calcul. Toute surface inférieure sera calculée comme cette valeur.",
    jhPerSquareMeter: "Nombre de jours-homme appliqué par m² pour estimer la base de travail liée à la surface.",
  },
  taskSettings: {
    minimumTasks: "Nombre minimal de tâches retenues. Toute valeur inférieure sera calculée comme ce minimum.",
    jhPerTask: "Temps moyen estimé pour relier une tâche planning à la logique BIM 4D.",
  },
  deliverables: {
    defaultPackageFactor: "Livrable de base inclus par défaut : maquette liée au planning et sortie PDF.",
    videoExportFactor: "Multiplicateur appliqué si le client demande des exports vidéo de la simulation 4D.",
    navisworksSynchroFactor: "Multiplicateur appliqué si le client demande une structuration ou exploitation dans Navisworks ou Synchro.",
  },
  siteFollowUp: {
    minimumWeeks: "Nombre minimum de semaines retenues pour le suivi chantier.",
    weeklyLight: "Charge hebdomadaire pour un suivi ponctuel avec mises à jour limitées.",
    weeklyStandard: "Charge hebdomadaire pour un suivi régulier avec mises à jour de maquette, planning et livrables.",
    weeklyIntensive: "Charge hebdomadaire pour un suivi intensif avec mises à jour fréquentes et coordination plus poussée.",
    sizeFactorThreshold: "Valeur utilisée pour mesurer la taille du projet à partir des JH après livrables. Plus ce seuil est bas, plus le facteur taille augmente rapidement.",
    sizeFactorMin: "Valeur minimale du facteur taille appliqué au suivi chantier.",
    sizeFactorMax: "Plafond du facteur taille pour éviter que le suivi chantier devienne excessif.",
  },
  rateTiers: {
    min: "Nombre de jours-homme à partir duquel un taux journalier spécifique est appliqué.",
    rate: "Prix HT par jour-homme utilisé pour calculer le montant estimatif. Le taux peut varier selon le volume total estimé.",
  },
};

const toNumber = (value) => (value === "" ? "" : Number(value));

const getTierRate = (days, tiers) => {
  const value = Number(days) || 0;
  for (const tier of tiers || []) {
    if (value >= tier.min) return tier.rate;
  }
  return 0;
};

export default function AdminBim4DPage() {
  const [mediaItems, setMediaItems] = useState([]);
  const [rules, setRules] = useState(() => cloneBim4DPricingRules());
  const [savedRules, setSavedRules] = useState(() => cloneBim4DPricingRules());
  const [updatedAt, setUpdatedAt] = useState("");
  const [mediaCarouselApi, setMediaCarouselApi] = useState();
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const inputRef = useRef(null);

  const placeholderSlides = [
    { id: "placeholder-simulation", title: "Simulation BIM 4D", icon: Clapperboard, mode: "placeholder" },
    { id: "placeholder-planning", title: "Planning chantier", icon: CalendarRange, mode: "placeholder" },
    { id: "placeholder-phasing", title: "Phasage projet", icon: TimerReset, mode: "placeholder" },
    { id: "placeholder-coordination", title: "Coordination temporelle", icon: Layers3, mode: "placeholder" },
  ];

  const fetchMedia = async () => {
    const API = getApiBase();
    const { data } = await axios.get(`${API}/api/admin/bim-4d/media`, { withCredentials: true });
    setMediaItems(data);
  };

  const fetchRules = async () => {
    const API = getApiBase();
    const { data } = await axios.get(`${API}/api/admin/bim-4d/pricing-rules`, { withCredentials: true });
    setRules(data);
    setSavedRules(data);
    setUpdatedAt(data.updatedAt || "");
  };

  useEffect(() => {
    fetchMedia().catch(() => toast.error("Impossible de charger les médias Hero 4D."));
    fetchRules().catch(() => toast.error("Impossible de charger les règles 4D."));
  }, []);

  useEffect(() => {
    if (!mediaCarouselApi) return undefined;
    const onSelect = () => setActiveMediaIndex(mediaCarouselApi.selectedScrollSnap());
    onSelect();
    mediaCarouselApi.on("select", onSelect);
    return () => mediaCarouselApi.off("select", onSelect);
  }, [mediaCarouselApi]);

  const mediaPreview = useMemo(() => mediaItems.map((item) => ({ ...item, previewUrl: `${getApiBase()}${item.url}`, mode: "media" })), [mediaItems]);
  const mediaSlides = mediaPreview.length ? mediaPreview : placeholderSlides;
  const activeMedia = mediaSlides[activeMediaIndex] || null;

  const updateRule = (section, key, value) => {
    setRules((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: toNumber(value),
      },
    }));
  };

  const updateRateTier = (index, key, value) => {
    setRules((current) => {
      const next = [...current.rateTiers];
      next[index] = { ...next[index], [key]: toNumber(value) };
      return { ...current, rateTiers: next };
    });
  };

  const validateRules = () => {
    const invalid = [];
    groups.forEach((group) => {
      Object.entries(rules[group.key] || {}).forEach(([key, value]) => {
        if (value === "" || Number.isNaN(Number(value)) || Number(value) < 0) invalid.push(`${group.title} / ${key}`);
      });
    });
    rules.rateTiers.forEach((tier, index) => {
      if (Number(tier.min) < 0 || Number(tier.rate) < 0) invalid.push(`Palier ${index + 1}`);
    });
    if ((rules.siteFollowUp?.sizeFactorMax ?? 0) < (rules.siteFollowUp?.sizeFactorMin ?? 0)) invalid.push("Facteur taille maximum");
    if ((rules.siteFollowUp?.sizeFactorThreshold ?? 0) <= 0) invalid.push("Seuil facteur taille");
    return invalid;
  };

  const handleSave = async () => {
    const invalid = validateRules();
    if (invalid.length) {
      toast.error(`Valeurs invalides : ${invalid[0]}`);
      return;
    }
    const API = getApiBase();
    await axios.put(`${API}/api/admin/bim-4d/pricing-rules`, rules, { withCredentials: true });
    toast.success("Règles de calcul 4D sauvegardées.");
    await fetchRules();
  };

  const handleReset = async () => {
    const API = getApiBase();
    await axios.post(`${API}/api/admin/bim-4d/pricing-rules/reset`, {}, { withCredentials: true });
    toast.success("Règles 4D réinitialisées aux valeurs par défaut.");
    await fetchRules();
  };

  const handleUploadMedia = async (files) => {
    if (!files.length) {
      toast.error("Ajoutez au moins un média avant l’envoi.");
      return;
    }
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const API = getApiBase();
        await axios.post(`${API}/api/admin/bim-4d/media`, formData, { withCredentials: true });
      }
      toast.success("Média Hero 4D importé.");
      if (inputRef.current) inputRef.current.value = "";
      await fetchMedia();
    } catch {
      toast.error("L’upload des médias 4D a échoué.");
    }
  };

  const handleDeleteMedia = async () => {
    if (!activeMedia || activeMedia.mode !== "media") return;
    const confirmed = window.confirm("Supprimer le média actuellement affiché ?");
    if (!confirmed) return;
    const API = getApiBase();
    await axios.delete(`${API}/api/admin/bim-4d/media/${activeMedia.id}`, { withCredentials: true });
    toast.success("Média Hero 4D supprimé.");
    setActiveMediaIndex(0);
    await fetchMedia();
  };

  const example4D = useMemo(() => {
    const surfaceRetained = Math.max(2000, Number(rules.surfaceSettings?.minimumSurface || 1000));
    const tasksRetained = Math.max(50, Number(rules.taskSettings?.minimumTasks || 20));
    const baseSurface = surfaceRetained * Number(rules.surfaceSettings?.jhPerSquareMeter || 0.003);
    const baseTasks = tasksRetained * Number(rules.taskSettings?.jhPerTask || 0.3);
    const base = baseSurface + baseTasks;
    const afterDeliverables = base * Number(rules.deliverables?.videoExportFactor || 1.2) * Number(rules.deliverables?.navisworksSynchroFactor || 1.8);
    const sizeFactor = Math.min(Number(rules.siteFollowUp?.sizeFactorMax || 2), Math.max(Number(rules.siteFollowUp?.sizeFactorMin || 1), afterDeliverables / Number(rules.siteFollowUp?.sizeFactorThreshold || 40)));
    const followUp = 8 * Number(rules.siteFollowUp?.weeklyStandard || 4) * sizeFactor;
    const totalDays = afterDeliverables + followUp;
    const applicableRate = getTierRate(totalDays, rules.rateTiers);
    return { surfaceRetained, tasksRetained, baseSurface, baseTasks, base, afterDeliverables, sizeFactor, followUp, totalDays, applicableRate, totalAmount: totalDays * applicableRate };
  }, [rules]);

  return (
    <TooltipProvider>
      <AdminLayout description="Gérez les paramètres publics du calculateur BIM 4D + Planning et les médias Hero de cette page." title="BIM 4D + Planning">
        <Tabs className="space-y-6" data-testid="admin-bim4d-tabs" defaultValue="rules">
          <TabsList className="grid w-full max-w-xl grid-cols-2 bg-white/88" data-testid="admin-bim4d-tabs-list">
            <TabsTrigger data-testid="admin-bim4d-tab-media" value="media">Médias du Hero</TabsTrigger>
            <TabsTrigger data-testid="admin-bim4d-tab-rules" value="rules">Règles de calcul</TabsTrigger>
          </TabsList>

          <TabsContent value="media">
            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-bim4d-media-simple-card">
              <CardContent className="p-8">
                <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Médias du Hero</div>
                {mediaPreview.length ? (
                  <div className="mt-6" data-testid="admin-bim4d-media-carousel-wrapper">
                    <Carousel className="mx-auto max-w-[1100px]" opts={{ loop: mediaSlides.length > 1 }} setApi={setMediaCarouselApi}>
                      <CarouselContent className="-ml-0">
                        {mediaSlides.map((item) => {
                          const Icon = item.icon;
                          return (
                            <CarouselItem className="pl-0" key={item.id}>
                              <div className="relative overflow-hidden rounded-[2rem] border border-[#d9e7d5] bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_22px_70px_-48px_rgba(15,118,110,0.2)]" data-testid={`admin-bim4d-carousel-media-${item.id}`}>
                                {item.mode === "media" ? (
                                  <img alt={item.filename} className="h-[24rem] w-full object-contain bg-[#f8fcf3] lg:h-[32rem]" data-testid={`admin-bim4d-media-preview-${item.id}`} src={item.previewUrl} />
                                ) : (
                                  <div className="flex h-[24rem] w-full flex-col items-center justify-center gap-4 bg-[linear-gradient(180deg,_rgba(230,243,241,0.88)_0%,_rgba(248,252,243,0.96)_100%)] text-center text-[#0f766e] lg:h-[32rem]">
                                    <Icon className="h-8 w-8" />
                                    <div>
                                      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0f766e]/70">Placeholder</div>
                                      <div className="mt-2 text-lg font-semibold text-slate-900">{item.title}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CarouselItem>
                          );
                        })}
                      </CarouselContent>
                    </Carousel>
                    <div className="mt-6 flex items-center justify-between gap-4" data-testid="admin-bim4d-media-carousel-nav">
                      <div className="flex items-center gap-2" data-testid="admin-bim4d-media-carousel-dots">
                        {mediaSlides.map((item, index) => (
                          <button className={`h-2.5 rounded-full transition-all ${activeMediaIndex === index ? "w-10 bg-[#0f766e]" : "w-2.5 bg-[#0f766e]/25 hover:bg-[#0f766e]/45"}`} data-testid={`admin-bim4d-media-dot-${index + 1}`} key={item.id} onClick={() => mediaCarouselApi?.scrollTo(index)} type="button" />
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim4d-media-prev-button" onClick={() => mediaCarouselApi?.scrollPrev()} type="button" variant="outline">Précédent</Button>
                        <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim4d-media-next-button" onClick={() => mediaCarouselApi?.scrollNext()} type="button" variant="outline">Suivant</Button>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-4" data-testid="admin-bim4d-media-actions">
                      <input accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="admin-bim4d-media-input" multiple onChange={(e) => handleUploadMedia(Array.from(e.target.files || []))} ref={inputRef} type="file" />
                      <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-bim4d-upload-button" onClick={() => inputRef.current?.click()} type="button">Upload</Button>
                      <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e] disabled:opacity-50" data-testid="admin-bim4d-delete-button" disabled={activeMedia?.mode !== "media"} onClick={handleDeleteMedia} type="button" variant="outline">Delete</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 rounded-[1.8rem] border border-dashed border-[#d9e7d5] bg-[#f8fcf3] px-6 py-16 text-center" data-testid="admin-bim4d-media-empty-state">
                    <div className="text-lg text-slate-500">Aucun média uploadé pour le moment</div>
                    <input accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="admin-bim4d-media-input" multiple onChange={(e) => handleUploadMedia(Array.from(e.target.files || []))} ref={inputRef} type="file" />
                    <Button className="mt-6 rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-bim4d-upload-button" onClick={() => inputRef.current?.click()} type="button">Upload</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rules">
            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-bim4d-rules-card">
              <CardContent className="p-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Règles du calculateur BIM 4D</div>
                    <div className="mt-2 text-sm text-slate-500" data-testid="admin-bim4d-rules-updated-at">Dernière modification : {updatedAt ? new Date(updatedAt).toLocaleString("fr-FR") : "-"}</div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim4d-rules-cancel-button" onClick={() => setRules(savedRules)} type="button" variant="outline">Annuler</Button>
                    <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim4d-rules-reset-button" onClick={handleReset} type="button" variant="outline">Réinitialiser les valeurs par défaut</Button>
                    <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-bim4d-rules-save-button" onClick={handleSave} type="button">Sauvegarder</Button>
                  </div>
                </div>

                <div className="mt-8 rounded-[1.8rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.95)_100%)] p-6" data-testid="admin-bim4d-formula-card">
                  <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Exemple de calcul BIM 4D</div>
                  <div className="mt-5 grid gap-6 xl:grid-cols-[1fr_0.95fr]">
                    <div className="rounded-[1.4rem] border border-[#d9e7d5] bg-white/90 p-5">
                      <h3 className="bim-display text-xl font-semibold text-slate-950">Formule générale simplifiée</h3>
                      <div className="mt-4 space-y-4 text-sm leading-7 text-slate-700">
                        <div><strong>Surface retenue</strong> = max(surface saisie, surface minimum)</div>
                        <div><strong>Tâches retenues</strong> = max(tâches saisies, minimum tâches)</div>
                        <div><strong>JH base</strong> = Surface retenue × coefficient JH/m² + Tâches retenues × coefficient JH/tâche</div>
                        <div><strong>JH après livrables</strong> = JH base × facteur exports vidéo si activé × facteur Navisworks/Synchro si activé</div>
                        <div><strong>Facteur taille suivi</strong> = min(facteur taille max, max(facteur taille min, JH après livrables / seuil facteur taille))</div>
                        <div><strong>JH suivi</strong> = semaines retenues × intensité hebdomadaire × facteur taille</div>
                        <div><strong>Total JH</strong> = JH après livrables + JH suivi</div>
                        <div><strong>Montant HT</strong> = Total JH × taux journalier applicable</div>
                      </div>
                    </div>
                    <div className="rounded-[1.4rem] border border-[#d9e7d5] bg-white/90 p-5" data-testid="admin-bim4d-formula-example">
                      <h3 className="bim-display text-xl font-semibold text-slate-950">Exemple chiffré</h3>
                      <div className="mt-4 space-y-2 text-sm text-slate-700">
                        <div>Surface saisie : <strong>2 000 m²</strong></div>
                        <div>Tâches saisies : <strong>50</strong></div>
                        <div>Exports vidéo : <strong>ON</strong></div>
                        <div>Navisworks/Synchro : <strong>ON</strong></div>
                        <div>Suivi chantier : <strong>ON</strong></div>
                        <div>Semaines : <strong>8</strong></div>
                        <div>Intensité : <strong>Standard</strong></div>
                      </div>
                      <div className="mt-5 space-y-3 rounded-[1.2rem] bg-[#f8fcf3] p-4 text-sm leading-7 text-slate-700">
                        <div><strong>Surface retenue</strong> = {example4D.surfaceRetained.toLocaleString("fr-FR")}</div>
                        <div><strong>Tâches retenues</strong> = {example4D.tasksRetained}</div>
                        <div><strong>JH base</strong> = {example4D.surfaceRetained.toLocaleString("fr-FR")} × {rules.surfaceSettings?.jhPerSquareMeter} + {example4D.tasksRetained} × {rules.taskSettings?.jhPerTask} = <strong>{example4D.base.toFixed(2)} JH</strong></div>
                        <div><strong>JH après livrables</strong> = {example4D.base.toFixed(2)} × {rules.deliverables?.videoExportFactor} × {rules.deliverables?.navisworksSynchroFactor} = <strong>{example4D.afterDeliverables.toFixed(2)} JH</strong></div>
                        <div><strong>Facteur taille</strong> = min({rules.siteFollowUp?.sizeFactorMax}, max({rules.siteFollowUp?.sizeFactorMin}, {example4D.afterDeliverables.toFixed(2)} / {rules.siteFollowUp?.sizeFactorThreshold})) = <strong>{example4D.sizeFactor.toFixed(2)}</strong></div>
                        <div><strong>JH suivi</strong> = 8 × {rules.siteFollowUp?.weeklyStandard} × {example4D.sizeFactor.toFixed(2)} = <strong>{example4D.followUp.toFixed(2)} JH</strong></div>
                        <div><strong>Total JH</strong> = {example4D.afterDeliverables.toFixed(2)} + {example4D.followUp.toFixed(2)} = <strong>{example4D.totalDays.toFixed(2)} JH</strong></div>
                        <div><strong>Montant HT</strong> = {example4D.totalDays.toFixed(2)} × {example4D.applicableRate} = <strong>{Math.round(example4D.totalAmount).toLocaleString("fr-FR")} € HT</strong></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 grid gap-6 xl:grid-cols-2">
                  {groups.map((group) => (
                    <Card className="rounded-[1.7rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_20px_60px_-46px_rgba(15,118,110,0.18)]" data-testid={`admin-bim4d-rules-group-${group.key}`} key={group.key}>
                      <CardContent className="p-6">
                        <h3 className="bim-display text-xl font-semibold text-slate-950">{group.title}</h3>
                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                          {Object.entries(rules[group.key] || {}).map(([key, value]) => (
                            <div key={key}>
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${group.key}-${key}`}>{key}</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button className="inline-flex text-[#0f766e]" data-testid={`admin-bim4d-help-${group.key}-${key}`} type="button"><HelpCircle className="h-4 w-4" /></button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm text-sm" data-testid={`admin-bim4d-help-tooltip-${group.key}-${key}`}>{fieldHelp[group.key]?.[key]}</TooltipContent>
                                </Tooltip>
                              </div>
                              <Input data-testid={`admin-bim4d-rule-${group.key}-${key}`} id={`${group.key}-${key}`} min={0} onChange={(e) => updateRule(group.key, key, e.target.value)} type="number" value={value} />
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <Card className="rounded-[1.7rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_20px_60px_-46px_rgba(15,118,110,0.18)] xl:col-span-2" data-testid="admin-bim4d-rate-tiers-card">
                    <CardContent className="p-6">
                      <h3 className="bim-display text-xl font-semibold text-slate-950">Paliers de taux journalier</h3>
                      <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {rules.rateTiers.map((tier, index) => (
                          <div className="rounded-2xl border border-[#d9e7d5] bg-white/80 p-4" data-testid={`admin-bim4d-rate-tier-${index + 1}`} key={`${tier.min}-${index}`}>
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0f766e]">Palier {index + 1}</div>
                            <div className="mt-3 grid gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Label htmlFor={`bim4d-rate-tier-min-${index}`}>Seuil de jours-homme</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button className="inline-flex text-[#0f766e]" data-testid={`admin-bim4d-help-rate-tier-min-${index + 1}`} type="button"><HelpCircle className="h-4 w-4" /></button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm text-sm">{fieldHelp.rateTiers.min}</TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input data-testid={`admin-bim4d-rate-tier-min-${index + 1}`} id={`bim4d-rate-tier-min-${index}`} min={0} onChange={(e) => updateRateTier(index, "min", e.target.value)} type="number" value={tier.min} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <Label htmlFor={`bim4d-rate-tier-rate-${index}`}>Taux journalier</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button className="inline-flex text-[#0f766e]" data-testid={`admin-bim4d-help-rate-tier-rate-${index + 1}`} type="button"><HelpCircle className="h-4 w-4" /></button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm text-sm">{fieldHelp.rateTiers.rate}</TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input data-testid={`admin-bim4d-rate-tier-rate-${index + 1}`} id={`bim4d-rate-tier-rate-${index}`} min={0} onChange={(e) => updateRateTier(index, "rate", e.target.value)} type="number" value={tier.rate} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </AdminLayout>
    </TooltipProvider>
  );
}